import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import type { AgendaComponentSortableItemProps } from "./AgendaComponentSortableItem";
import { useNestedComponent } from "@repo/ui/components/Base/hooks/useNestedComponent";

export type SortableItem<T> = React.ComponentType<
  AgendaComponentSortableItemProps<T>
>;

export interface SortableListProps<T> {
  items: (T & { isEditable?: boolean })[];
  onChange: (items: T[]) => void;
  onEdit?: (item: T) => void;
  sortableItem: SortableItem<T>;
  editableLabel?: string;
  notEditableLabel?: string;
  deleteLabel?: string;
  disabled?: boolean;
  renderTags?: (item: T) => React.ReactNode;
}

export const SortableList = <T extends { id: string }>({
  items,
  onChange,
  onEdit,
  sortableItem: SortableItem,
  editableLabel,
  notEditableLabel,
  deleteLabel,
  disabled,
  renderTags,
}: SortableListProps<T>) => {
  SortableItem = useNestedComponent(SortableItem);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!active || !over) {
      return;
    }

    if (active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      onChange(arrayMove(items, oldIndex, newIndex));
    }
  }

  const handleMoveUp = (item: T) => {
    const oldIndex = items.findIndex((i) => i.id === item.id);
    const newIndex = oldIndex - 1;
    onChange(arrayMove(items, oldIndex, newIndex));
  };

  const handleMoveDown = (item: T) => {
    const oldIndex = items.findIndex((i) => i.id === item.id);
    const newIndex = oldIndex + 1;
    onChange(arrayMove(items, oldIndex, newIndex));
  };

  const handleEdit = (item: T) => {
    onEdit && onEdit(item);
  };

  const handleDelete = (item: T) => {
    const oldIndex = items.findIndex((i) => i.id === item.id);
    const newItems = [...items];
    newItems.splice(oldIndex, 1);
    onChange(newItems);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items}
        strategy={verticalListSortingStrategy}
        disabled={disabled}
      >
        {items.map((item, index) => {
          const { isEditable, ...sortable } = item;
          const typedSortable = sortable as unknown as T;

          // filter out nulls that occur due to missing data during load
          if (sortable.id == null) return null;

          return (
            <SortableItem
              disabled={disabled}
              key={sortable.id}
              item={typedSortable}
              onMoveUp={() => handleMoveUp(typedSortable)}
              onMoveDown={() => handleMoveDown(typedSortable)}
              disableMoveUp={index === 0}
              disableMoveDown={index === items.length - 1}
              onEdit={() => handleEdit(typedSortable)}
              onDelete={() => handleDelete(typedSortable)}
              isEditable={isEditable}
              editableLabel={editableLabel}
              notEditableLabel={notEditableLabel}
              deleteLabel={deleteLabel}
              renderTags={renderTags}
            />
          );
        })}
      </SortableContext>
    </DndContext>
  );
};
